import React from "react";

/** ---------- tiny shared helpers (no side effects) ---------- */

const isObj = (v) => v && typeof v === "object" && !Array.isArray(v);
const isBinaryString = (s) => typeof s === "string" && s.startsWith("__BINARY__");

const safeJson = (txt) => {
    if (txt == null || txt === "") return { ok: false, value: undefined, err: "empty" };
    if (typeof txt === "object") return { ok: true, value: txt, err: null };
    if (isBinaryString(txt)) return { ok: false, value: undefined, err: "binary content" };
    try { return { ok: true, value: JSON.parse(txt), err: null }; }
    catch (e) { return { ok: false, value: undefined, err: e.message }; }
};


const getEncoding = (req) =>
    (
        req?.contentEncoding ||
        req?.responseHeaders?.["content-encoding"] ||
        req?.responseHeaders?.["Content-Encoding"] ||
        "identity"
    ).toLowerCase();

const getMime = (req) => {
    const ct = (
        req?.responseContentType ||
        req?.responseHeaders?.["content-type"] ||
        req?.responseHeaders?.["Content-Type"] ||
        ""
    ).toLowerCase();
    return ct.split(";")[0];
};

const fmtBytes = (n) => {
    if (n == null) return "";
    if (n < 1024) return `${n} B`;
    const u = ["KB", "MB", "GB"];
    let i = -1, v = n;
    do { v /= 1024; i++; } while (v >= 1024 && i < u.length - 1);
    return `${v.toFixed(v < 10 ? 2 : v < 100 ? 1 : 0)} ${u[i]}`;
};
const fmtMs = (n) => (n || n === 0) ? `${Math.round(n)} ms` : "";

const stringify = (v) => {
    if (v == null) return "";
    if (typeof v === "string") return v;
    try { return JSON.stringify(v); } catch { return String(v); }
};


/** parse simple x-www-form-urlencoded / querystring-like body into a key/value map */
function parseFormBody(raw) {
    if (!raw || typeof raw !== "string") return null;
    if (!raw.includes("=")) return null;
    const out = {};
    const src = raw.trim();
    src.split("&").forEach((pair) => {
        if (!pair) return;
        const [kPart, ...vParts] = pair.split("=");
        const key = kPart || "";
        const value = vParts.join("=");
        if (!key) return;
        const normKey = key.replace(/\+/g, " ");
        const normVal = (value || "").replace(/\+/g, " ");
        try {
            const dk = decodeURIComponent(normKey);
            const dv = decodeURIComponent(normVal);
            out[dk] = dv;
        } catch {
            out[key] = value || "";
        }
    });
    return Object.keys(out).length ? out : null;
}

/**
 * Parse nested querystring-like values such as `cust_params`, handling the case where the
 * inner payload is itself URL-encoded (e.g. us_privacy%3D1YNN%26app_version%3D...).
 */
function parseNestedParams(raw) {
    if (!raw || typeof raw !== "string") return null;
    // Strip leading `?` if present.
    let src = raw.startsWith("?") ? raw.slice(1) : raw;

    // First try to parse as-is (handles unencoded "key=val&..." strings).
    let parsed = parseFormBody(src);
    if (parsed) return parsed;

    // If that fails, try decoding once and parsing again (covers %3D / %26 patterns).
    try {
        const decoded = decodeURIComponent(src.replace(/\+/g, " "));
        parsed = parseFormBody(decoded);
        if (parsed) return parsed;
    } catch {
        // ignore decode errors and fall through
    }

    return null;
}


const hostOf = (url = "") => { try { return new URL(url).host.replace(/^www\./, ""); } catch { return ""; } };

/** flatten object into path->string for field-by-field compare (arrays included) */
function flatten(obj, prefix = "", out = {}) {
    if (obj == null) { out[prefix || "(empty)"] = ""; return out; }
    if (typeof obj !== "object") { out[prefix || "(value)"] = obj; return out; }
    if (Array.isArray(obj)) {
        if (obj.length === 0) out[prefix || "(array)"] = "[]";
        obj.forEach((v, i) => flatten(v, prefix ? `${prefix}[${i}]` : `[${i}]`, out));
        return out;
    }
    const keys = Object.keys(obj);
    if (keys.length === 0) out[prefix || "(object)"] = "{}";
    keys.forEach((k) => flatten(obj[k], prefix ? `${prefix}.${k}` : k, out));
    return out;
}

/** merge two maps of fields into rows */
function buildFieldRows(aMap, bMap) {
    const keys = Array.from(new Set([...Object.keys(aMap), ...Object.keys(bMap)])).sort();
    return keys.map((k) => {
        const av = aMap[k] ?? "—";
        const bv = bMap[k] ?? "—";
        const status = av === bv ? "same" : (aMap[k] == null ? "added" : (bMap[k] == null ? "removed" : "changed"));
        return { key: k, a: av, b: bv, status };
    });
}

/**
 * For JSON bodies that contain a string `cust_params`, also expose a decoded view so that
 * JSON diff mode can show `cust_params.*` keys instead of one long encoded string.
 */
function withDecodedCustParams(obj) {
    if (!obj || typeof obj !== "object" || Array.isArray(obj)) return obj;
    const out = { ...obj };
    if (typeof out.cust_params === "string") {
        const parsed = parseNestedParams(out.cust_params);
        if (parsed) {
            // Preserve the original for reference, but replace `cust_params` with a nested map.
            out.cust_params_raw = out.cust_params;
            out.cust_params = parsed;
        }
    }
    return out;
}

/** normalize headers to simple lower-case map */
function normalizeHeaders(h = {}) {
    if (Array.isArray(h)) {
        const out = {};
        h.forEach((x) => {
            const k = String(x?.name || x?.key || "").toLowerCase();
            if (!k) return;
            out[k] = x?.value ?? x?.val ?? "";
        });
        return out;
    }
    const out = {};
    Object.keys(h || {}).forEach((k) => (out[k.toLowerCase()] = h[k]));
    return out;
}

/** highlight helper */
const escapeRe = (s = "") => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
function highlight(text, q) {
    if (!q) return text;
    const s = String(text ?? "");
    if (!s) return s;
    const re = new RegExp(escapeRe(q), "ig");
    const parts = [];
    let last = 0; let m;
    while ((m = re.exec(s))) {
        parts.push(s.slice(last, m.index));
        parts.push(<mark key={m.index}>{m[0]}</mark>);
        last = m.index + m[0].length;
    }
    parts.push(s.slice(last));
    return parts;
}

/**
 * Download a JSON file helper
 */
function downloadJsonFile(filename, data) {
    const blob = new Blob([JSON.stringify(data, null, 2)], {
        type: "application/json",
    });

    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
}

/** ---------- UI ---------- */

export default function CompareRequestsModal({ open, onClose, a, b, onClearSelection }) {
    const [tab, setTab] = React.useState("response"); // 'request' | 'response'
    const [search, setSearch] = React.useState("");
    const [diffsOnly, setDiffsOnly] = React.useState(true);
    const [overridePair, setOverridePair] = React.useState(null);
    const fileInputRef = React.useRef(null);

    const effectiveA = overridePair?.a || a;
    const effectiveB = overridePair?.b || b;

    const aJ = safeJson(effectiveA?.responseBody);
    const bJ = safeJson(effectiveB?.responseBody);
    const aRqJ = safeJson(effectiveA?.requestBody);
    const bRqJ = safeJson(effectiveB?.requestBody);

    const aRqParsedFromProxy = isObj(effectiveA?.requestBodyParsed) ? effectiveA.requestBodyParsed : null;
    const bRqParsedFromProxy = isObj(effectiveB?.requestBodyParsed) ? effectiveB.requestBodyParsed : null;

    const aRqParsed =
        (isObj(aRqParsedFromProxy) && Object.keys(aRqParsedFromProxy).length > 0)
            ? aRqParsedFromProxy
            : parseFormBody(effectiveA?.requestBody);

    const bRqParsed =
        (isObj(bRqParsedFromProxy) && Object.keys(bRqParsedFromProxy).length > 0)
            ? bRqParsedFromProxy
            : parseFormBody(effectiveB?.requestBody);

    // Treat as "form/body diff" whenever we can derive a parsed body, either from the proxy
    // or by parsing the raw string locally. This covers IMA + other form-encoded bodies.
    const isFormReq = !!(aRqParsed || bRqParsed);

    const reqBodyMode = aRqJ.ok && bRqJ.ok
        ? "json"
        : isFormReq
            ? "form"
            : "raw";

    const sameHost =
        hostOf(effectiveA?.url) && hostOf(effectiveA?.url) === hostOf(effectiveB?.url);

    // --- Decoded cust_params logic ---
    const aCustRaw = (aRqParsed && typeof aRqParsed.cust_params === "string") ? aRqParsed.cust_params : null;
    const bCustRaw = (bRqParsed && typeof bRqParsed.cust_params === "string") ? bRqParsed.cust_params : null;

    const aCustParsed = parseNestedParams(aCustRaw);
    const bCustParsed = parseNestedParams(bCustRaw);

    const aRqParsedSansCust = aRqParsed ? { ...aRqParsed } : null;
    const bRqParsedSansCust = bRqParsed ? { ...bRqParsed } : null;
    if (aRqParsedSansCust && Object.prototype.hasOwnProperty.call(aRqParsedSansCust, "cust_params")) {
        delete aRqParsedSansCust.cust_params;
    }
    if (bRqParsedSansCust && Object.prototype.hasOwnProperty.call(bRqParsedSansCust, "cust_params")) {
        delete bRqParsedSansCust.cust_params;
    }

    const hasDecodedCustParams = !!(aCustParsed || bCustParsed);

    // Decoded cust_params for JSON request bodies (e.g., GMA/GAM, Prebid JSON)
    const aRqJson = aRqJ.ok && isObj(aRqJ.value) ? aRqJ.value : null;
    const bRqJson = bRqJ.ok && isObj(bRqJ.value) ? bRqJ.value : null;

    const aRqJsonCustRaw = (aRqJson && typeof aRqJson.cust_params === "string") ? aRqJson.cust_params : null;
    const bRqJsonCustRaw = (bRqJson && typeof bRqJson.cust_params === "string") ? bRqJson.cust_params : null;

    const aRqJsonCustParsed = parseNestedParams(aRqJsonCustRaw);
    const bRqJsonCustParsed = parseNestedParams(bRqJsonCustRaw);

    const aRqJsonSansCust = aRqJson ? { ...aRqJson } : null;
    const bRqJsonSansCust = bRqJson ? { ...bRqJson } : null;

    if (aRqJsonSansCust && Object.prototype.hasOwnProperty.call(aRqJsonSansCust, "cust_params")) {
        delete aRqJsonSansCust.cust_params;
    }
    if (bRqJsonSansCust && Object.prototype.hasOwnProperty.call(bRqJsonSansCust, "cust_params")) {
        delete bRqJsonSansCust.cust_params;
    }

    const hasJsonDecodedCustParams = !!(aRqJsonCustParsed || bRqJsonCustParsed);

    const handleExportCompare = () => {
        const payload = {
            meta: {
                exportedAt: new Date().toISOString(),
                sameHost: !!sameHost,
            },
            a: effectiveA,
            b: effectiveB,
        };

        const filename = `compare-${Date.now().toString()}.json`;
        downloadJsonFile(filename, payload);
    };

    const handleImportCompare = (event) => {
        const input = event.target;
        const file = input && input.files && input.files[0];
        if (!file) return;

        const reader = new FileReader();
        reader.onload = () => {
            try {
                const parsed = JSON.parse(reader.result || "{}");
                // Expecting the shape { meta, a, b }, but be lenient
                const nextA = parsed.a || parsed.left || null;
                const nextB = parsed.b || parsed.right || null;
                if (nextA && nextB) {
                    setOverridePair({ a: nextA, b: nextB });
                } else {
                    console.error("Imported compare JSON missing 'a' or 'b' payload");
                }
            } catch (e) {
                console.error("Failed to parse compare JSON file", e);
            } finally {
                // allow selecting the same file again
                if (input) input.value = "";
            }
        };
        reader.readAsText(file);
    };

    const handleImportClick = () => {
        if (fileInputRef.current) {
            fileInputRef.current.click();
        }
    };

    if (!open) return null;

    const h = styles;

    const row = (label, av, bv) => (
        <tr>
            <td style={h.k}>{label}</td>
            <td style={h.v}>{av ?? "—"}</td>
            <td style={h.v}>{bv ?? "—"}</td>
        </tr>
    );

    /** request meta */
    const RequestBlock = (
        <div style={{ display: "grid", gap: 12 }}>
            <section>
                <div style={h.sub}>General</div>
                <table style={h.table}>
                    <tbody>
                        {row("Method", effectiveA?.method, effectiveB?.method)}
                        {row("Status", effectiveA?.status ?? effectiveA?.responseCode, effectiveB?.status ?? effectiveB?.responseCode)}
                        {row("URL", effectiveA?.url, effectiveB?.url)}
                        {row("Type", effectiveA?.type, effectiveB?.type)}
                        {row("Size", fmtBytes(effectiveA?.size), fmtBytes(effectiveB?.size))}
                        {row("Total", fmtMs(effectiveA?.durationMs), fmtMs(effectiveB?.durationMs))}
                        {row("Resp Content-Type", getMime(effectiveA) || "", getMime(effectiveB) || "")}
                        {row("Resp Content-Encoding", getEncoding(effectiveA), getEncoding(effectiveB))}
                    </tbody>
                </table>
            </section>

            <section>
                <div style={h.sub}>Request headers</div>
                <DiffTable
                    a={normalizeHeaders(effectiveA?.requestHeaders)}
                    b={normalizeHeaders(effectiveB?.requestHeaders)}
                    search={search}
                    diffsOnly={diffsOnly}
                />
            </section>

            <section>
                <div style={h.sub}>
                    Request body
                    <span style={h.help}>
                        {reqBodyMode === "json"
                            ? " (JSON diff)"
                            : reqBodyMode === "form"
                                ? " (form/body diff)"
                                : " (raw compare)"}
                    </span>
                </div>
                {reqBodyMode === "json" ? (
                    <div style={{ display: "grid", gap: 8 }}>
                        <FieldsDiff
                            aObj={aRqJsonSansCust || aRqJ.value}
                            bObj={bRqJsonSansCust || bRqJ.value}
                            search={search}
                            diffsOnly={diffsOnly}
                        />
                        {hasJsonDecodedCustParams && (
                            <div>
                                <div style={{ ...h.help, margin: "6px 0 4px 0" }}>cust_params (decoded)</div>
                                <DiffTable
                                    a={aRqJsonCustParsed || {}}
                                    b={bRqJsonCustParsed || {}}
                                    search={search}
                                    diffsOnly={diffsOnly}
                                />
                            </div>
                        )}
                    </div>
                ) : reqBodyMode === "form" ? (
                    <div style={{ display: "grid", gap: 8 }}>
                        <DiffTable
                            a={aRqParsedSansCust || {}}
                            b={bRqParsedSansCust || {}}
                            search={search}
                            diffsOnly={diffsOnly}
                        />
                        {hasDecodedCustParams && (
                            <div>
                                <div style={{ ...h.help, margin: "6px 0 4px 0" }}>cust_params (decoded)</div>
                                <DiffTable
                                    a={aCustParsed || {}}
                                    b={bCustParsed || {}}
                                    search={search}
                                    diffsOnly={diffsOnly}
                                />
                            </div>
                        )}
                    </div>
                ) : (
                    <SideBySide
                        aVal={stringify(effectiveA?.requestBody)}
                        bVal={stringify(effectiveB?.requestBody)}
                        search={search}
                    />
                )}
            </section>
        </div>
    );

    /** response meta */
    const ResponseBlock = (
        <div style={{ display: "grid", gap: 12 }}>
            <section>
                <div style={h.sub}>Response headers</div>
                <DiffTable
                    a={normalizeHeaders(effectiveA?.responseHeaders)}
                    b={normalizeHeaders(effectiveB?.responseHeaders)}
                    search={search}
                    diffsOnly={diffsOnly}
                />
            </section>

            <section>
                <div style={h.sub}>
                    Response body
                    <span style={h.help}>{aJ.ok && bJ.ok ? " (JSON diff)" : " (raw compare)"}</span>
                </div>
                {aJ.ok && bJ.ok ? (
                    <FieldsDiff aObj={aJ.value} bObj={bJ.value} search={search} diffsOnly={diffsOnly} />
                ) : (
                    <SideBySide
                        aVal={stringify(effectiveA?.responseBody)}
                        bVal={stringify(effectiveB?.responseBody)}
                        search={search}
                    />
                )}
            </section>
        </div>
    );

    return (
        <div style={h.overlay} onClick={onClose}>
            <div style={h.modal} onClick={(e) => e.stopPropagation()}>
                <div style={h.header}>
                    <div style={{ display: "flex", flexDirection: "column" }}>
                        <strong>Compare Requests</strong>
                    </div>
                    <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                        <input
                            placeholder="Search path or value"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            style={h.search}
                        />
                        <label style={h.chk}>
                            <input type="checkbox" checked={diffsOnly} onChange={(e) => setDiffsOnly(e.target.checked)} />
                            <span>Show diffs only</span>
                        </label>
                        <input
                            type="file"
                            accept="application/json,.json"
                            ref={fileInputRef}
                            onChange={handleImportCompare}
                            style={{ display: "none" }}
                        />
                        <button onClick={handleImportClick} style={h.btn}>Import JSON</button>
                        <button onClick={() => setTab("request")} style={{ ...h.tab, ...(tab === "request" ? h.tabOn : {}) }}>Request</button>
                        <button onClick={() => setTab("response")} style={{ ...h.tab, ...(tab === "response" ? h.tabOn : {}) }}>Response</button>
                        <button onClick={handleExportCompare} style={h.btn}>Export JSON</button>
                        {onClearSelection && (
                            <button onClick={onClearSelection} style={h.btn} title="Clear selected pairs in the list">Clear selection</button>
                        )}
                        <button onClick={onClose} style={h.btn}>Close</button>
                    </div>
                </div>

                <div style={{ padding: 12 }}>
                    {tab === "request" ? RequestBlock : ResponseBlock}
                </div>
            </div>
        </div>
    );
}

/** ---------- pieces ---------- */

function DiffTable({ a = {}, b = {}, search = "", diffsOnly = false }) {
    const h = styles;

    // Step 1: Build rows with status + values
    const keys = Array.from(new Set([...Object.keys(a), ...Object.keys(b)])).sort();
    const term = search.trim().toLowerCase();
    const rows = keys
        .map((k) => {
            const av = a[k];
            const bv = b[k];
            const same = String(av ?? "") === String(bv ?? "");
            const status = same ? "same" : (av == null ? "added" : (bv == null ? "removed" : "changed"));
            return { k, av, bv, status, same };
        })
        .filter((r) => {
            if (diffsOnly && r.same) return false;
            if (!term) return true;
            return (
                r.k.toLowerCase().includes(term) ||
                String(r.av ?? "").toLowerCase().includes(term) ||
                String(r.bv ?? "").toLowerCase().includes(term)
            );
        });

    // Step 2: Build suffixToAdapter map from rows
    const suffixToAdapter = {};
    rows.forEach((r) => {
        if (typeof r.k === "string" && r.k.startsWith("hb_bidder_")) {
            const suffix = r.k.slice("hb_bidder_".length);
            // Canonical name: prefer value (av or bv), fallback to suffix
            const val = String(r.av ?? r.bv ?? "").trim();
            const canonical = val || suffix;
            if (suffix) suffixToAdapter[suffix] = canonical;
        }
    });

    // Step 3: Helper to extract canonical adapter name from hb_* key
    function extractAdapterFromHbKey(key, suffixToAdapter) {
        if (typeof key !== "string" || !key.startsWith("hb_")) return null;
        const tokens = key.split("_");
        if (tokens.length < 3) return null;
        const last = tokens[tokens.length - 1];
        // List of generic tokens to ignore as adapters
        const generic = [
            "id", "ids", "path", "paths", "host", "hosts", "size", "sizes", "env", "format", "formats",
            "pb", "pbs", "cache", "key", "keys", "req", "reqs", "bidder", "bidders"
        ];
        if (generic.includes(last)) return null;

        if (!suffixToAdapter) {
            return last;
        }

        // 1) Exact match from hb_bidder_* map
        if (suffixToAdapter[last]) {
            return suffixToAdapter[last];
        }

        const suffixKeys = Object.keys(suffixToAdapter);
        if (!suffixKeys.length) {
            return last;
        }

        // 2) Fuzzy match: find a suffix whose key shares a prefix with `last`.
        //    This handles cases like thetra/thetrade/thetradede/thetradedesk,
        //    or rubico/rubicon, without hard-coding SSP names.
        let bestKey = null;
        suffixKeys.forEach((sk) => {
            if (sk === last) return;
            if (sk.startsWith(last) || last.startsWith(sk)) {
                // prefer the longest matching key for stability
                if (!bestKey || sk.length > bestKey.length) {
                    bestKey = sk;
                }
            }
        });

        if (bestKey && suffixToAdapter[bestKey]) {
            return suffixToAdapter[bestKey];
        }

        // 3) Fallback: use the suffix itself as adapter name (works for unknown SSPs)
        return last;
    }

    // Step 4: Grouping logic for hb_ keys
    const hasHbAdapters = rows.some(r => extractAdapterFromHbKey(r.k, suffixToAdapter));
    if (!rows.length) return <div style={h.empty}>(none)</div>;

    // Helper to render a table for a set of rows
    function renderTable(rows, keyPrefix) {
        return (
            <div style={{ overflowX: "auto" }}>
                <table style={h.tableWide}>
                    <thead>
                        <tr>
                            <th style={h.th}>Key</th>
                            <th style={h.th}>A</th>
                            <th style={h.th}>B</th>
                            <th style={h.th}>Status</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((r) => {
                            const tone = r.same ? h.ok : (r.status === "changed" ? h.changed : h.warn);
                            return (
                                <tr key={keyPrefix + r.k}>
                                    <td style={h.k}>{highlight(r.k, search)}</td>
                                    <td style={h.v}>{highlight(String(r.av ?? "—"), search)}</td>
                                    <td style={h.v}>{highlight(String(r.bv ?? "—"), search)}</td>
                                    <td style={{ ...h.v, ...tone }}>{r.status}</td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        );
    }

    if (!hasHbAdapters) {
        // No grouping needed, regular table
        return renderTable(rows, "");
    }

    // Group rows by canonical adapter name
    const globalRows = [];
    const grouped = new Map();
    rows.forEach((r) => {
        const adapter = extractAdapterFromHbKey(r.k, suffixToAdapter);
        if (!adapter) {
            globalRows.push(r);
        } else {
            if (!grouped.has(adapter)) grouped.set(adapter, []);
            grouped.get(adapter).push(r);
        }
    });

    return (
        <div style={{ display: "grid", gap: 12 }}>
            {globalRows.length > 0 && renderTable(globalRows, "global-")}
            {Array.from(grouped.keys()).sort().map((adapter) => (
                <div key={adapter}>
                    <div style={{ ...h.sub, margin: "8px 0 4px 0" }}>Adapter: {adapter}</div>
                    {renderTable(grouped.get(adapter) || [], adapter + "-")}
                </div>
            ))}
        </div>
    );
}

function FieldsDiff({ aObj = {}, bObj = {}, search = "", diffsOnly = false }) {
    const h = styles;
    const rows = buildFieldRows(
        flatten(withDecodedCustParams(aObj)),
        flatten(withDecodedCustParams(bObj))
    ).filter((r) => {
        if (diffsOnly && r.status === "same") return false;
        if (!search) return true;
        const term = search.toLowerCase();
        return (
            r.key.toLowerCase().includes(term) ||
            String(r.a).toLowerCase().includes(term) ||
            String(r.b).toLowerCase().includes(term)
        );
    });

    if (!rows.length) return <div style={h.empty}>(empty)</div>;

    // Grouping Logic
    const groups = {
        "General": []
    };

    // Priority order for known top-level keys
    const PRIORITIES = ["imp", "app", "site", "device", "user", "regs", "source", "ext", "cust_params"];

    rows.forEach(r => {
        // Extract top-level key from dot notation (e.g. "app.content" -> "app")
        // Also handle array notation "imp[0]" -> "imp"
        const topKey = r.key.split('.')[0].split('[')[0];

        let targetGroup = "General";
        if (PRIORITIES.includes(topKey) || r.key.startsWith(topKey + '.')) {
            // Capitalize for display
            targetGroup = topKey.charAt(0).toUpperCase() + topKey.slice(1);
        } else if (topKey === "cust_params") {
            targetGroup = "Custom Params";
        }

        if (!groups[targetGroup]) groups[targetGroup] = [];
        groups[targetGroup].push(r);
    });

    // Sort groups: Priorities first, then alphabetical, then General/Other
    const sortedGroupKeys = Object.keys(groups).sort((a, b) => {
        const idxA = PRIORITIES.indexOf(a.toLowerCase());
        const idxB = PRIORITIES.indexOf(b.toLowerCase());

        if (idxA !== -1 && idxB !== -1) return idxA - idxB;
        if (idxA !== -1) return -1;
        if (idxB !== -1) return 1;

        if (a === "General") return -1; // General at top if mostly unknown, or bottom? User usually wants context first.
        if (b === "General") return 1;

        return a.localeCompare(b);
    });

    // Helper to render table for a group
    const renderTable = (groupRows) => (
        <div style={{ overflowX: "auto" }}>
            <table style={h.tableWide}>
                <thead>
                    <tr>
                        <th style={h.th}>Path</th>
                        <th style={h.th}>A</th>
                        <th style={h.th}>B</th>
                        <th style={h.th}>Status</th>
                    </tr>
                </thead>
                <tbody>
                    {groupRows.map((r) => {
                        const tone = r.status === "same" ? h.ok : (r.status === "changed" ? h.changed : h.warn);
                        return (
                            <tr key={r.key}>
                                <td style={h.k}>{highlight(r.key, search)}</td>
                                <td style={h.v}>{highlight(String(r.a), search)}</td>
                                <td style={h.v}>{highlight(String(r.b), search)}</td>
                                <td style={{ ...h.v, ...tone }}>{r.status}</td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );

    return (
        <div style={{ display: "grid", gap: 16 }}>
            {sortedGroupKeys.map(groupName => {
                const groupRows = groups[groupName];
                if (groupRows.length === 0) return null;
                return (
                    <div key={groupName}>
                        <div style={{
                            ...h.sub,
                            margin: "0 0 4px 0",
                            borderBottom: '1px solid var(--line)',
                            paddingBottom: 4,
                            color: 'var(--primary)',
                            fontSize: '13px'
                        }}>
                            {groupName} <span style={{ fontSize: '11px', color: 'var(--muted)', fontWeight: 'normal' }}>({groupRows.length})</span>
                        </div>
                        {renderTable(groupRows)}
                    </div>
                );
            })}
        </div>
    );
}

function SideBySide({ aVal, bVal, search = "" }) {
    const h = styles;
    return (
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
            <pre style={h.code}>{highlight(aVal || "(empty)", search)}</pre>
            <pre style={h.code}>{highlight(bVal || "(empty)", search)}</pre>
        </div>
    );
}

/** ---------- styles ---------- */

const styles = {
    overlay: {
        position: "fixed", inset: 0, background: "rgba(0,0,0,.35)", zIndex: 9999,
        display: "flex", alignItems: "center", justifyContent: "center", padding: 16
    },
    modal: {
        width: "min(1200px, 98vw)", maxHeight: "92vh", overflow: "auto",
        background: "var(--card)", border: "1px solid var(--line)", borderRadius: 12,
        boxShadow: "0 10px 30px rgba(0,0,0,.25)", color: "var(--ink)"
    },
    header: {
        padding: "10px 12px", borderBottom: "1px solid var(--line)", background: "var(--bg)",
        display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, flexWrap: "wrap",
        color: "var(--ink)"
    },
    sub: { fontWeight: 600, marginBottom: 6, color: "var(--ink)" },
    help: { color: "var(--muted)", marginLeft: 6, fontSize: 12 },
    btn: {
        padding: "6px 10px", fontSize: 12, border: "1px solid var(--line)",
        borderRadius: 8, background: "var(--card)", cursor: "pointer", color: "var(--ink)"
    },
    tab: {
        padding: "6px 10px", fontSize: 12, border: "1px solid transparent",
        borderRadius: 8, background: "var(--bg)", cursor: "pointer", color: "var(--muted)"
    },
    tabOn: { background: "var(--card)", border: "1px solid var(--line)", color: "var(--ink)" },
    search: { width: 220, padding: "6px 8px", border: "1px solid var(--line)", borderRadius: 8, fontSize: 12, background: "var(--bg)", color: "var(--ink)" },
    chk: { display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12, color: "var(--muted)" },
    table: { width: "100%", borderCollapse: "collapse", fontSize: 12, color: "var(--ink)" },
    tableWide: { width: "100%", borderCollapse: "collapse", fontSize: 12, tableLayout: "fixed", color: "var(--ink)" },
    th: { textAlign: "left", padding: "6px 8px", borderBottom: "1px solid var(--line)", color: "var(--muted)" },
    k: {
        width: 260,
        padding: "6px 8px",
        borderBottom: "1px solid var(--line)",
        color: "var(--ink)",
        verticalAlign: "top",
        whiteSpace: "normal",
        wordBreak: "break-word",
        overflowWrap: "anywhere",
    },
    v: { padding: "6px 8px", borderBottom: "1px solid var(--line)", verticalAlign: "top", wordBreak: "break-word", color: "var(--ink)" },
    code: {
        border: "1px solid var(--line)", borderRadius: 10, background: "var(--bg)",
        padding: 12, maxHeight: 420, overflow: "auto",
        fontFamily: "var(--font-mono)",
        fontSize: 12, lineHeight: 1.4, whiteSpace: "pre-wrap", overflowWrap: "anywhere", wordBreak: "break-word", color: "var(--ink)"
    },
    ok: { color: "var(--success-text)" },
    warn: { color: "var(--warn-text)" },
    changed: { color: "var(--error-text)" },
    empty: { color: "var(--muted)", fontStyle: "italic" },
};
